import { Component, Input, ViewChild } from '@angular/core';
import { Store, createSelector } from '@ngrx/store';
import { AutoComplete } from 'primeng/autocomplete';
import { BehaviorSubject, combineLatest, map } from 'rxjs';
import { FreyaCommonModule } from 'src/app/freya-common/freya-common.module';
import { LibModule } from 'src/app/lib.ts/lib.module';
import { ControlMetaDirective } from 'src/app/new-opportunity/control-meta.directive';
import { NewOpportunityCustomerActions } from 'src/app/new-opportunity/new-opportunity-customer/new-opportunity-customer.actions';

import { FullUserFragment } from '../../../../generated/graphql.generated';
import { JobCreateCustomerActions } from '../../job-create/job-create-customer/job-create-customer.actions';
import { jobCreateCustomerFeature } from '../../job-create/job-create-customer/job-create-customer.feature';
import { CustomerEditorField, JobCreateCustomerState } from '../../job-create/job-create-customer/job-create-customer.state';
import { EMAIL_PATTERN, PHONE_PATTERN } from '../../job-helpers';
import { selectCustomerEditable } from '../../job-tool.reducer';

export type SearchUsersContext = 'createCustomer' | 'newOpportunity';

@Component({
    selector: 'app-job-search-users',
    standalone: true,
    imports: [
        FreyaCommonModule,
        LibModule,
        ControlMetaDirective,
    ],
    templateUrl: './job-search-users.component.html',
    styleUrl: './job-search-users.component.scss'
})
export class JobSearchUsersComponent {

    @ViewChild(AutoComplete) autocomplete: AutoComplete;

    @Input() label: string = '';
    @Input() placeholder: string = '';
    @Input() tabIndex: number = 0;
    @Input() context: SearchUsersContext = 'createCustomer';

    private _field$ = new BehaviorSubject<keyof JobCreateCustomerState['customerEditor'] | null>(null);

    @Input() set field(value: keyof JobCreateCustomerState['customerEditor']) {
        this._field$.next(value);
    }

    constructor(
        public store: Store,
    ) { }

    public viewModel$ = combineLatest([
        this._field$,
        this.store.select(createSelector(
            jobCreateCustomerFeature.selectCustomerEditor,
            selectCustomerEditable,
            (customerEditor, customerEditable) => ({
                customerEditor,
                customerEditable,
            })
        )),
    ]).pipe(
        map(([ field, { customerEditor, customerEditable } ]) => {

            if (!field) {
                return {
                    field: null as string | null,
                    value: '',
                    suggestedCustomers: [],
                    activeColumns: { email: false, phone: false, company: false },
                    customerEditable,
                    pattern: null as RegExp | null,
                };
            }

            const suggestedCustomers: FullUserFragment[] = customerEditor?.suggestedCustomers?.[field] ?? [];

            const activeColumns = {
                email: suggestedCustomers.some((c) => c.email),
                phone: suggestedCustomers.some((c) => c.phone),
                company: suggestedCustomers.some((c) => c.company),
            };

            return {
                field,
                value: customerEditor?.[field] || '',
                suggestedCustomers,
                activeColumns,
                customerEditable,
                pattern: field === 'phone' ? PHONE_PATTERN : field === 'email' ? EMAIL_PATTERN : null,
                isFocused: field === customerEditor?.focusedField,
            };
        })
    );

    getKeyUpAction(input: { field: string; value: string; key?: string }) {
        return this.context === 'newOpportunity'
            ? NewOpportunityCustomerActions.customerAutocompleteInputChanged(input)
            : JobCreateCustomerActions.customerAutocompleteInputChanged(input);
    }

    getSearchFiredAction(input: { query: string; field: CustomerEditorField }) {
        return this.context === 'newOpportunity'
            ? NewOpportunityCustomerActions.customerAutocompleteSearchFired(input)
            : JobCreateCustomerActions.customerAutocompleteSearchFired(input);
    }

    getFocusAction(input: { field: CustomerEditorField }) {
        return this.context === 'newOpportunity'
            ? NewOpportunityCustomerActions.customerAutocompleteInputFocused(input)
            : JobCreateCustomerActions.customerAutocompleteInputFocused(input);
    }

    getBlurAction(input: { field: CustomerEditorField }) {
        return this.context === 'newOpportunity'
            ? NewOpportunityCustomerActions.customerAutocompleteInputBlur(input)
            : JobCreateCustomerActions.customerAutocompleteInputBlur(input);
    }

    getSelectCustomerAction(input: { existingCustomer: FullUserFragment }) {
        return this.context === 'newOpportunity'
            ? NewOpportunityCustomerActions.existingCustomerSelected(input)
            : JobCreateCustomerActions.existingCustomerSelected(input);
    }

    hideDropdownIfBlurred(isFocused: boolean) {
        if (!isFocused) {
            this.autocomplete?.hide();
        }
    }
}
